import { Button, Chip, List, ListItem, Menu, Stack, Tooltip, Typography } from "@mui/material";
import { MdsIcon } from "@sinch/components";
import { MarginCategory } from "@sinch/core/entities/serverEnums";
import { CheckboxInput } from "@sinch/components/form/CheckboxInput/CheckboxInput";
import { MdsKeyboardArrowDown } from "@sinch/icons";
import { includes } from "ramda";
import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { isNilOrEmpty } from "ramda-adjunct";

interface CategoryFilterProps {
  availableFilters: (MarginCategory | `${MarginCategory}`)[];
  categoryFilter: Set<MarginCategory | `${MarginCategory}`>;
  setCategoryFilter: React.Dispatch<React.SetStateAction<Set<MarginCategory | `${MarginCategory}`>>>;
}

export const CategoryFilter: FC<CategoryFilterProps> = ({ categoryFilter, setCategoryFilter, availableFilters }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggle = (categories: MarginCategory[], checked: boolean) => {
    setCategoryFilter((prev) => {
      const next = new Set(prev);
      categories.forEach((c) => (checked ? next.add(c) : next.delete(c)));
      return next;
    });
  };

  if (availableFilters.length === 1) {
    return null;
  }

  const activeLabels: { id: MarginCategory[]; label: string }[] = [];
  if (categoryFilter.has(MarginCategory.Attendance) && includes(MarginCategory.Attendance, availableFilters)) {
    activeLabels.push({ id: [MarginCategory.Attendance], label: t("Attendance.title") });
  }
  if (categoryFilter.has(MarginCategory.Tag) && includes(MarginCategory.Tag, availableFilters)) {
    activeLabels.push({ id: [MarginCategory.Tag], label: t("CategoryFilter.tags") });
  }
  if (
    (categoryFilter.has(MarginCategory.TransportThere) || categoryFilter.has(MarginCategory.TransportBack)) &&
    (includes(MarginCategory.TransportThere, availableFilters) ||
      includes(MarginCategory.TransportBack, availableFilters))
  ) {
    activeLabels.push({
      id: [MarginCategory.TransportThere, MarginCategory.TransportBack],
      label: t("CategoryFilter.transport"),
    });
  }

  return (
    <>
      <Stack alignItems="center" direction="row" height="100%" spacing={0.5} width="100%">
        <Typography
          className="MuiDataGrid-columnHeaderTitle TableLikeDataGrid-columnHeaderTitle"
          noWrap
          sx={{ overflow: "visible" }}
        >
          {t("CategoryFilter.marginsOf")}:{" "}
        </Typography>
        <Tooltip title={t("filters")}>
          <Button
            color="inherit"
            fullWidth
            onClick={handleOpen}
            size="small"
            sx={{
              p: 0.5,
              textTransform: "none",
              minWidth: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Stack alignItems="center" direction="row" height="100%" spacing={1}>
              {isNilOrEmpty(activeLabels) && (
                <Typography
                  className="MuiDataGrid-columnHeaderTitle TableLikeDataGrid-columnHeaderTitle"
                  noWrap
                  sx={{
                    overflow: "visible",
                    ".TableLikeDataGrid-columnHeader &.TableLikeDataGrid-columnHeaderTitle": { color: "error.main" },
                  }}
                >
                  {t("none")}
                </Typography>
              )}
              {activeLabels.map(({ id, label }) => (
                <Chip
                  key={id.join(",")}
                  label={label}
                  onDelete={(e) => {
                    e.preventDefault();
                    toggle(id, false);
                  }}
                  size="small"
                  sx={{ px: 0, fontWeight: 700, fontSize: "0.8125rem" }}
                />
              ))}
            </Stack>
            <MdsIcon fontSize="xs" icon={MdsKeyboardArrowDown} />
          </Button>
        </Tooltip>
      </Stack>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        autoFocus={false}
        onClose={handleClose}
        open={open}
        sx={{ zIndex: 10000 }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
      >
        <List disablePadding>
          {includes(MarginCategory.Attendance, availableFilters) && (
            <ListItem sx={{ py: 0 }}>
              <CheckboxInput
                label={t("CategoryFilter.attendance")}
                onChange={(_, checked) => toggle([MarginCategory.Attendance], checked)}
                size="small"
                value={categoryFilter.has(MarginCategory.Attendance)}
              />
            </ListItem>
          )}
          {includes(MarginCategory.Tag, availableFilters) && (
            <ListItem sx={{ py: 0 }}>
              <CheckboxInput
                label={t("CategoryFilter.tags")}
                onChange={(_, checked) => toggle([MarginCategory.Tag], checked)}
                size="small"
                value={categoryFilter.has(MarginCategory.Tag)}
              />
            </ListItem>
          )}
          {(includes(MarginCategory.TransportThere, availableFilters) ||
            includes(MarginCategory.TransportBack, availableFilters)) && (
            <ListItem sx={{ py: 0 }}>
              <CheckboxInput
                label={t("CategoryFilter.transport")}
                onChange={(_, checked) =>
                  toggle([MarginCategory.TransportThere, MarginCategory.TransportBack], checked)
                }
                size="small"
                value={categoryFilter.has(MarginCategory.TransportThere)}
              />
            </ListItem>
          )}
        </List>
      </Menu>
    </>
  );
};
